/**
 * §6.6/§6.6a/§6.6b — the affiliate's own declared-channels editor.
 *
 * `affiliate_traffic_sources` has existed since migration 0050 with no
 * writer anywhere, so this screen has never shown anything but an empty
 * list — and the proposal-time warning A3 built (§7.14b) could never fire
 * for real, because nothing put a row here to warn against. This is that
 * writer.
 *
 * §6.6a: a declared channel is a contractual statement, not a survey — so
 * saving replaces the whole list rather than merging into it, the same
 * "set, not patch" shape `setAffiliateTags` already uses. §6.6b: a channel
 * already verified stays verified across an edit that leaves it in place —
 * enforced server-side (`setMyTrafficSources`, `traffic-sources.ts`), which
 * only ever touches the rows that actually changed.
 */
import { useEffect, useState } from 'react';

import {
  Callout,
  Empty,
  Field,
  Loading,
  Select,
  TextInput,
  explainFailure,
  formatDate,
  kitText,
  useLoad,
} from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { t, type AffiliateMessageKey } from './messages.ts';

interface Row {
  readonly channel: string;
  readonly property_url: string | null;
  /** `null` for a row added locally and not yet saved. */
  readonly declared_at: string | null;
  readonly verified_at: string | null;
}

/** A bare code, translated. Every code this build knows already has a word for it (§7.14's own catalogue). */
function channelLabel(code: string): string {
  return t(`trafficChannel.${code}` as AffiliateMessageKey);
}

export function TrafficSources({ api }: { api: AffiliateApi }) {
  const current = useLoad(() => api.trafficSources(), []);

  const [rows, setRows] = useState<readonly Row[] | null>(null);
  const [newChannel, setNewChannel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  // The form was write-only otherwise: opened on nothing whatever had been
  // saved, the same trap `PayoutPreference` guards against a few screens
  // over.
  useEffect(() => {
    if (current.state !== 'ready') return;
    setRows(current.value.declared.map((d) => ({ ...d })));
  }, [current]);

  if (current.state === 'loading' || rows === null) return <Loading rows={3} />;
  if (current.state === 'error') {
    return <Callout tone="error">{explainFailure(current.error).message}</Callout>;
  }

  // Offered for a *new* row: the catalogue's active codes, minus whatever is
  // already on this list — a channel added once cannot be added twice, and
  // a withdrawn channel never appears here at all unless it is already on
  // `rows` (0172/0206's "carried, not offered" convention — enforced
  // server-side; this list only ever shows what the server already agreed to
  // offer).
  const declaredChannels = new Set(rows.map((r) => r.channel));
  const offered = current.value.active_channels.filter((c) => !declaredChannels.has(c));
  const pickerValue = offered.includes(newChannel) ? newChannel : (offered[0] ?? '');

  const addChannel = () => {
    const channel = pickerValue;
    if (!channel) return;
    setSaved(null);
    setRows([
      ...rows,
      { channel, property_url: newUrl.trim() || null, declared_at: null, verified_at: null },
    ]);
    setNewChannel('');
    setNewUrl('');
  };

  const removeChannel = (channel: string) => {
    setSaved(null);
    setRows(rows.filter((r) => r.channel !== channel));
  };

  const setPropertyUrl = (channel: string, url: string) => {
    setRows(rows.map((r) => (r.channel === channel ? { ...r, property_url: url } : r)));
  };

  const save = () => {
    setBusy(true);
    setFailure(null);
    api
      .setTrafficSources(rows.map((r) => ({ channel: r.channel, property_url: r.property_url })))
      .then((r) => {
        setSaved(r.note);
        setRows(r.declared.map((d) => ({ ...d })));
      })
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <div className="form">
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <Callout fold={false}>{t('trafficSources.intro')}</Callout>

      {rows.length === 0 ? (
        <Empty>{t('trafficSources.empty')}</Empty>
      ) : (
        rows.map((row) => (
          <div className="form-row" key={row.channel} style={{ alignItems: 'flex-end' }}>
            <Field label={channelLabel(row.channel)}>
              <TextInput
                value={row.property_url ?? ''}
                onChange={(v) => setPropertyUrl(row.channel, v)}
                placeholder="https://…"
              />
            </Field>
            <button type="button" className="copy" onClick={() => removeChannel(row.channel)}>
              {t('trafficSources.remove')}
            </button>
            {row.declared_at ? (
              <p className="faint">
                {t('trafficSources.declaredOn', { date: formatDate(row.declared_at) })}
                {' — '}
                {row.verified_at
                  ? t('trafficSources.verifiedOn', { date: formatDate(row.verified_at) })
                  : t('trafficSources.notYetVerified')}
              </p>
            ) : null}
          </div>
        ))
      )}

      <div className="form-row">
        <Field label={t('trafficSources.addChannel')}>
          <Select
            value={pickerValue}
            onChange={setNewChannel}
            options={offered.map((c) => ({ value: c, label: channelLabel(c) }))}
          />
        </Field>
        <Field label={t('trafficSources.propertyUrl')} hint={t('trafficSources.propertyUrl.hint')}>
          <TextInput value={newUrl} onChange={setNewUrl} placeholder="https://…" />
        </Field>
        <div className="form-actions">
          <button type="button" className="copy" disabled={!pickerValue} onClick={addChannel}>
            {t('trafficSources.add')}
          </button>
        </div>
      </div>
      {offered.length === 0 ? <p className="faint">{t('trafficSources.noneOffered')}</p> : null}

      <div className="form-actions">
        <button type="button" className="primary" disabled={busy} onClick={save}>
          {busy ? kitText('action.saving') : t('trafficSources.save')}
        </button>
      </div>

      {saved ? <Callout fold={false}>{saved}</Callout> : null}
    </div>
  );
}
